import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney, convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getListingPriceWithReolinkPromoMaybe } from '../../util/reolinkPromoPriceDisplay';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 500 });

const PriceMaybe = props => {
  const {
    price,
    publicData,
    config,
    intl,
    listingTypeConfig,
    brand,
    strikeThroughPrice = null,
    promoDiscountFormatted = null,
  } = props;
  const showPrice = displayPrice(listingTypeConfig);

  // `brand` is already a finalized display label (deslugified by the parent).
  // Render it here, but decoupled from the price: the brand is the visible part
  // of this block (the price is shown via the image overlay) and must never be
  // dropped just because a listing type hides its price.
  const brandNode = brand ? <div className={css.brandInfo}>{brand}</div> : null;

  if (!showPrice && price) {
    return brandNode ? <div className={css.priceAndBrand}>{brandNode}</div> : null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  let formattedStrikeThrough = null;
  if (
    strikeThroughPrice &&
    price &&
    strikeThroughPrice.currency === price.currency &&
    strikeThroughPrice.amount > price.amount
  ) {
    try {
      formattedStrikeThrough = formatMoney(intl, strikeThroughPrice);
    } catch (e) {
      formattedStrikeThrough = null;
    }
  }
  const showPromoRow = !!(formattedStrikeThrough && promoDiscountFormatted);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.priceAndBrand}>
      {brandNode}
      <div className={css.price} title={priceTitle}>
        {showPromoRow && !hasMultiplePriceVariants ? (
          <div className={css.priceWithSale}>
            <div>
              <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
            </div>
            <div className={css.campaignPromoStrikeRow}>
              <span className={css.campaignDiscountBadge}>-{promoDiscountFormatted}</span>
              <span className={css.originalPrice}>{formattedStrikeThrough}</span>
            </div>
          </div>
        ) : hasMultiplePriceVariants ? (
          <FormattedMessage
            id="ListingCard.priceStartingFrom"
            values={{ priceValue, pricePerUnit }}
          />
        ) : (
          <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
        )}
      </div>
    </div>
  );
};

const PriceOverlayMaybe = props => {
  const {
    price,
    publicData,
    config,
    intl,
    listingTypeConfig,
    strikeThroughPrice = null,
    promoDiscountFormatted = null,
  } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  if (!formattedPrice) {
    return null;
  }

  let formattedStrikeThrough = null;
  if (
    strikeThroughPrice &&
    price &&
    strikeThroughPrice.currency === price.currency &&
    strikeThroughPrice.amount > price.amount
  ) {
    try {
      formattedStrikeThrough = formatMoney(intl, strikeThroughPrice);
    } catch (e) {
      formattedStrikeThrough = null;
    }
  }
  const showPromoOverlay = !!(
    formattedStrikeThrough &&
    promoDiscountFormatted &&
    !hasMultiplePriceVariants
  );

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.priceBadge} title={priceTitle}>
      {showPromoOverlay ? (
        <div className={css.priceBadgePromoStack}>
          <div className={css.priceBadgePromoStrikeRow}>
            <span className={css.priceBadgePromoPill}>-{promoDiscountFormatted}</span>
            <span className={css.priceBadgeOriginalStruck}>{formattedStrikeThrough}</span>
          </div>
          <div>
            <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
          </div>
        </div>
      ) : hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showBrandInfo
 * @param {string?} props.linkTarget
 * @param {string?} props.linkRel
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const location = useLocation();
  const currentUser = useSelector(state => state.user?.currentUser);

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showBrandInfo = true,
    linkTarget,
    linkRel,
  } = props;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const { listingType, cardStyle, brand } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  // Find the brand field configuration to get enum options
  const brandFieldConfig = config.listing.listingFields?.find(field => field.key === 'brand');
  const brandEnumOptions = brandFieldConfig?.enumOptions || [];

  // Get the display name for the brand value
  const getBrandDisplayName = (brandValue) => {
    if (!brandValue) return null;
    // Handle array case (brand can be an array)
    const brandStr = Array.isArray(brandValue) ? brandValue[0] : brandValue;
    if (typeof brandStr !== 'string') return brandStr;
    const brandOption = brandEnumOptions.find(option => option.option === brandStr);
    if (brandOption) return brandOption.label;
    // Deslugify brand: replace hyphens with spaces and capitalize words
    return brandStr
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const brandDisplayName = getBrandDisplayName(brand);

  const connectivity = publicData?.connectivity;

  // Helper function to get display value from config
  const getDisplayValue = (fieldKey, value) => {
    const fieldConfig = config.listing.listingFields?.find(field => field.key === fieldKey);
    if (!fieldConfig || !fieldConfig.enumOptions) {
      // If no config, just return the value (capitalize first letter for display)
      if (typeof value === 'string') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(v => {
        const option = fieldConfig.enumOptions.find(opt => opt.option === v);
        return option ? option.label : (typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v);
      });
    } else {
      const option = fieldConfig.enumOptions.find(opt => opt.option === value);
      return option ? option.label : (typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1) : value);
    }
  };

  // Format connectivity for display with +X more logic
  const formatConnectivity = (connectivityArray) => {
    if (!connectivityArray || !Array.isArray(connectivityArray) || connectivityArray.length === 0) {
      return null;
    }
    
    const displayValues = getDisplayValue('connectivity', connectivityArray);
    if (displayValues.length === 1) {
      return displayValues[0];
    } else if (displayValues.length === 2) {
      // If 2 connectivities, display both with &
      return `${displayValues[0]} & ${displayValues[1]}`;
    } else if (displayValues.length > 2) {
      // If more than 2, display first & X others
      const remainingCount = displayValues.length - 1;
      const otherLabel = remainingCount === 1 ? 'autre' : 'autres';
      return `${displayValues[0]} & ${remainingCount} ${otherLabel}`;
    }
    return null;
  };

  const connectivityFormatted = formatConnectivity(connectivity);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const sales = publicData?.sales;
  const isOnSale =
    sales === 1 || sales === true || sales === 'true' || sales === '1';
  const cardPrice = isOnSale
    ? price
    : getListingPriceWithReolinkPromoMaybe(currentListing, {
        currentUser,
        locationSearch: location.search,
      }) || price;
  const campaignStrikeThroughPrice =
    !isOnSale &&
    price &&
    cardPrice &&
    price.currency === cardPrice.currency &&
    price.amount !== cardPrice.amount
      ? price
      : null;
  let campaignPromoDiscountFormatted = null;
  if (
    campaignStrikeThroughPrice &&
    price &&
    cardPrice &&
    price.currency === cardPrice.currency &&
    price.amount > cardPrice.amount
  ) {
    try {
      campaignPromoDiscountFormatted = formatMoney(
        intl,
        new Money(price.amount - cardPrice.amount, price.currency)
      );
    } catch (error) {
      // Silently handle error
    }
  }

  const originalPrice = publicData?.original_price;
  let merchantDiscountFormatted = null;
  if (isOnSale && originalPrice && price) {
    try {
      const subUnitDivisor = unitDivisor(price.currency);
      const originalPriceInSubunits = convertUnitToSubUnit(originalPrice, subUnitDivisor);
      if (originalPriceInSubunits > price.amount) {
        merchantDiscountFormatted = formatMoney(
          intl,
          new Money(originalPriceInSubunits - price.amount, price.currency)
        );
      }
    } catch (error) {
      // Silently handle error
    }
  }

  const classes = classNames(rootClassName || css.root, className, {
    [css.hasDiscountBadge]: !!(merchantDiscountFormatted || campaignPromoDiscountFormatted),
  });

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      target={linkTarget}
      rel={linkRel}
    >
      {merchantDiscountFormatted ? (
        <span className={css.cardDiscountBadge}>-{merchantDiscountFormatted}</span>
      ) : campaignPromoDiscountFormatted ? (
        <span className={css.cardDiscountBadge}>-{campaignPromoDiscountFormatted}</span>
      ) : null}
      <div className={css.cardWrapper}>
        <PriceOverlayMaybe
          price={cardPrice}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
          strikeThroughPrice={campaignStrikeThroughPrice}
          promoDiscountFormatted={campaignPromoDiscountFormatted}
        />
        <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
        />
      </div>
      <div className={css.info}>
        <PriceMaybe
          price={cardPrice}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
          brand={brandDisplayName}
          strikeThroughPrice={campaignStrikeThroughPrice}
          promoDiscountFormatted={campaignPromoDiscountFormatted}
        />
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
          )}
        </div>
        {connectivityFormatted && (
          <div className={css.categoryConnectivityInfo}>
            <span className={css.connectivityText}>{connectivityFormatted}</span>
          </div>
        )}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
